import React from "react";
import { LOGO_URL, dash } from "./formatters";

// ---------- Design-Konstanten (GDV-Antrag-Style) --------------------------
// Minimalistisch, viel Whitespace, fast-Schwarz auf Weiß, Cyan nur als
// Akzent in Eyebrows. Vorbild: GDV-Antrag-PDF aus services/gdv_request_letter.py
export const COLORS = {
  ink: "#0B1320",       // Fast-Schwarz für Hauptelemente
  body: "#1f2937",      // Body-Text
  mute: "#65718A",      // Sekundärtext / Labels
  accent: "#0891B2",    // Cyan nur für Eyebrows / Akzentlinien
  light: "#F1F5F9",     // Tabellenlabels Background
  border: "#E5E7EB",    // Trennlinien sehr hell
  white: "#FFFFFF",
};
export const FONT_STACK = "'Helvetica', 'Arial', sans-serif";

// ---------- Layout-Bausteine ----------------------------------------------
export function A4Page({ children, pageNo, totalPages, doc }) {
  return (
    <div
      className="doc-a4 bg-white shadow-sm mx-auto relative"
      style={{
        width: "210mm",
        minHeight: "297mm",
        padding: "22mm 20mm 24mm 20mm",
        fontFamily: FONT_STACK,
        fontSize: "10pt",
        lineHeight: 1.5,
        color: COLORS.body,
        marginBottom: "10mm",
      }}
    >
      <PageHeader doc={doc} />
      <div style={{ paddingTop: "10mm" }}>{children}</div>
      <PageFooter pageNo={pageNo} totalPages={totalPages} doc={doc} />
    </div>
  );
}

export function PageHeader({ doc }) {
  const hasUserLogo = !!doc.letterheadUrl;
  return (
    <div
      style={{
        position: "absolute",
        top: "10mm",
        left: "20mm",
        right: "20mm",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        paddingBottom: "3mm",
        borderBottom: `0.5pt solid ${COLORS.border}`,
        fontSize: "8.5pt",
        color: COLORS.mute,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        {hasUserLogo ? (
          <img src={doc.letterheadUrl} alt={doc.gutachterCompany || "Logo"}
               style={{ height: "11mm", width: "auto", maxWidth: "60mm", objectFit: "contain" }} />
        ) : (
          <>
            <img src={LOGO_URL} alt="Easy Gutachter" style={{ height: "8mm", width: "auto" }} />
            <span style={{ fontWeight: 700, color: COLORS.ink, letterSpacing: "0.01em", fontSize: "9.5pt" }}>
              Easy Gutachter
            </span>
          </>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", fontSize: "8pt", gap: "1.5mm", textAlign: "right", color: COLORS.mute }}>
        <div style={{ display: "flex", gap: "10px" }}>
          <span>AZ: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{dash(doc.caseRef)}</span></span>
          <span>SN: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{dash(doc.schadenNr)}</span></span>
          <span>VN: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{dash(doc.licensePlate)}</span></span>
        </div>
        {(doc.phone || doc.email) && (
          <div style={{ fontSize: "7.5pt", color: COLORS.mute }}>
            {doc.phone && <span>TEL {doc.phone}</span>}
            {doc.phone && doc.email && <span style={{ margin: "0 6px" }}>·</span>}
            {doc.email && <span>{doc.email}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export function PageFooter({ pageNo, totalPages, doc }) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: "12mm",
        left: "20mm",
        right: "20mm",
        borderTop: `0.5pt solid ${COLORS.border}`,
        paddingTop: "3mm",
        fontSize: "7.5pt",
        color: COLORS.mute,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        lineHeight: 1.5,
      }}
    >
      <div>
        <div style={{ fontWeight: 700, color: COLORS.ink }}>
          {dash(doc.gutachterName)}{doc.gutachterCompany ? ` · ${doc.gutachterCompany}` : ""}
        </div>
        <div>{dash(doc.gutachterAddress)}</div>
        <div style={{ marginTop: "0.5mm" }}>
          {doc.bankName && <span>{doc.bankName} · </span>}
          {doc.iban && <span>IBAN {doc.iban}</span>}
          {doc.iban && doc.bic && <span> · </span>}
          {doc.bic && <span>BIC {doc.bic}</span>}
          {(doc.iban || doc.bic) && doc.vatId && <span> · </span>}
          {doc.vatId && <span>USt-ID {doc.vatId}</span>}
        </div>
      </div>
      <div style={{ color: COLORS.ink, fontWeight: 600 }}>
        {pageNo} / {totalPages}
      </div>
    </div>
  );
}

// "Eyebrow" + "H2" pattern (wie ACCENT-eyebrow + INK-H1 im GDV-Antrag)
export function SectionTitle({ children, eyebrow }) {
  return (
    <div style={{ marginTop: "6mm", marginBottom: "4mm" }}>
      {eyebrow && (
        <div style={{
          fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
          letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1mm",
        }}>{eyebrow}</div>
      )}
      <h2 style={{
        fontSize: "16pt", fontWeight: 700, color: COLORS.ink,
        margin: 0, lineHeight: 1.2, letterSpacing: "-0.005em",
      }}>{children}</h2>
    </div>
  );
}

export function SubTitle({ children }) {
  return (
    <h3 style={{
      fontSize: "10pt", fontWeight: 700, color: COLORS.ink,
      marginTop: "5mm", marginBottom: "2mm", letterSpacing: "0",
    }}>
      {children}
    </h3>
  );
}

// Key-Value Table (Label-Spalte mit LIGHT-Background, Value-Spalte weiß)
export function KVList({ rows }) {
  const visible = rows.filter((r) => r && r[1] !== null && r[1] !== undefined && r[1] !== "");
  if (visible.length === 0) return null;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt", marginTop: "2mm" }}>
      <tbody>
        {visible.map(([label, value], i) => (
          <tr key={i}>
            <td style={{
              padding: "2.5mm 4mm", color: COLORS.body, width: "55mm",
              verticalAlign: "top", background: COLORS.light,
              fontWeight: 700, borderBottom: `1pt solid ${COLORS.white}`,
            }}>{label}</td>
            <td style={{
              padding: "2.5mm 4mm", color: COLORS.ink, verticalAlign: "top",
              whiteSpace: "pre-line", background: COLORS.white,
            }}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Banner-Row in der Zusammenfassung & Ergebnis — analog zu KVList: nur die
// Label-Spalte (links) hat grauen Hintergrund, die Value-Spalte (rechts)
// bleibt weiß. Das matcht den GDV-Antrag-Datenblock.
export function SummaryRow({ label, value, accent = false }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt" }}>
      <tbody>
        <tr>
          <td style={{
            padding: "3mm 4mm", width: "55mm", verticalAlign: "top",
            background: COLORS.light, color: COLORS.body, fontWeight: 700,
            borderBottom: `1pt solid ${COLORS.white}`,
          }}>{label}</td>
          <td style={{
            padding: "3mm 4mm", textAlign: "right", verticalAlign: "top",
            background: COLORS.white, color: COLORS.ink,
            fontWeight: accent ? 800 : 600,
            fontVariantNumeric: "tabular-nums",
          }}>{value}</td>
        </tr>
      </tbody>
    </table>
  );
}

export function ValueTile({ label, value, accent = false, small = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        // 6pt + 0% letterSpacing → auch die längsten deutschen Komposita
        // (WIEDERBESCHAFFUNGSDAUER = 24 Zeichen) passen in eine Zeile.
        fontSize: "6pt", letterSpacing: "0.02em", color: COLORS.mute,
        fontWeight: 700, textTransform: "uppercase", marginBottom: "1.5mm",
        // Falls ein Label im Sonderfall doch zu lang ist, bricht es
        // sauber zwischen Wörtern (kein Mittendrin-Split mehr).
        overflowWrap: "break-word",
        wordBreak: "normal",
        lineHeight: 1.25,
        // Mindesthöhe für Konsistenz zwischen Tiles in einer Reihe.
        minHeight: "4mm",
      }}
      lang="de"
      >{label}</div>
      <div style={{
        fontSize: small ? "9pt" : "12pt",
        fontWeight: accent ? 800 : 700,
        color: accent ? COLORS.accent : COLORS.ink,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.15,
        overflowWrap: "anywhere",
      }}>{value}</div>
    </div>
  );
}

// Kosten-Tabelle (Reparaturkosten breakdown)
export function CostsTable({ rows, totalLabel, totalValue }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "3mm", fontSize: "10pt" }}>
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i} style={{ borderBottom: `0.3pt solid ${COLORS.border}` }}>
            <td style={{ padding: "2mm 0", color: COLORS.body }}>{label}</td>
            <td style={{ padding: "2mm 0", textAlign: "right", fontVariantNumeric: "tabular-nums", color: COLORS.ink }}>{value}</td>
          </tr>
        ))}
        <tr style={{ borderTop: `1pt solid ${COLORS.ink}` }}>
          <td style={{ padding: "3mm 0 0", fontWeight: 700, color: COLORS.ink, fontSize: "10.5pt" }}>{totalLabel}</td>
          <td style={{ padding: "3mm 0 0", textAlign: "right", fontWeight: 700, color: COLORS.ink, fontVariantNumeric: "tabular-nums", fontSize: "10.5pt" }}>{totalValue}</td>
        </tr>
      </tbody>
    </table>
  );
}

// Detail-Positionen-Tabelle (gruppiert nach Kategorie) — kompakt, mit
// Position-Beschreibung, Menge × Einzelpreis und Netto-Summe rechts.
export function PositionsTable({ title, items, currencySuffix = " €" }) {  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: "5mm" }}>
      <div style={{
        fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
        letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1.5mm",
      }}>{title} · {items.length} Position{items.length === 1 ? "" : "en"}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9pt" }}>
        <thead>
          <tr style={{ borderBottom: `0.5pt solid ${COLORS.ink}` }}>
            <th style={{ textAlign: "left", padding: "2mm 0", color: COLORS.mute, fontWeight: 700, width: "8mm" }}>#</th>
            <th style={{ textAlign: "left", padding: "2mm 0", color: COLORS.mute, fontWeight: 700 }}>Beschreibung</th>
            <th style={{ textAlign: "right", padding: "2mm 0", color: COLORS.mute, fontWeight: 700, width: "18mm" }}>Menge</th>
            <th style={{ textAlign: "right", padding: "2mm 0", color: COLORS.mute, fontWeight: 700, width: "22mm" }}>Einzel</th>
            <th style={{ textAlign: "right", padding: "2mm 0", color: COLORS.mute, fontWeight: 700, width: "26mm" }}>Netto</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id || i} style={{ borderBottom: `0.3pt solid ${COLORS.border}` }}>
              <td style={{ padding: "1.8mm 0", color: COLORS.mute }}>{i + 1}</td>
              <td style={{ padding: "1.8mm 0", color: COLORS.body }}>{it.description || "—"}</td>
              <td style={{ padding: "1.8mm 0", textAlign: "right", fontVariantNumeric: "tabular-nums", color: COLORS.body }}>
                {Number(it.quantity || 0).toLocaleString("de-DE")} {it.unit_label || ""}
              </td>
              <td style={{ padding: "1.8mm 0", textAlign: "right", fontVariantNumeric: "tabular-nums", color: COLORS.body }}>
                {Number(it.unit_price || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{currencySuffix}
              </td>
              <td style={{ padding: "1.8mm 0", textAlign: "right", fontVariantNumeric: "tabular-nums", color: COLORS.ink, fontWeight: 600 }}>
                {Number(it.total_net || (it.quantity * it.unit_price) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{currencySuffix}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
